import { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Trophy, 
  DollarSign, 
  TrendingUp, 
  X,
  PartyPopper,
  Share2,
  Download
} from 'lucide-react';
import confetti from 'canvas-confetti';

/**
 * WinSummary - A celebratory micro-moment component
 * 
 * Shows when a user achieves a significant milestone:
 * - Closing a deal in CRM
 * - Getting a proposal accepted
 * - Receiving invoice payment
 * - Reaching revenue milestones
 * 
 * Usage:
 * <WinSummary 
 *   type="deal_won" 
 *   data={{ clientName: "Acme Corp", dealValue: 5000 }} 
 *   onClose={() => setShowWin(false)} 
 * />
 */

const winTypes = {
  deal_won: {
    title: "Deal Won!",
    icon: Trophy,
    color: "text-yellow-400",
    bgColor: "bg-yellow-500/20",
    message: (data) => `You closed ${data.clientName} for ${formatCurrency(data.dealValue)}!`
  },
  proposal_accepted: {
    title: "Proposal Accepted!",
    icon: PartyPopper,
    color: "text-green-400",
    bgColor: "bg-green-500/20",
    message: (data) => `${data.clientName} accepted your ${formatCurrency(data.value)} proposal!`
  },
  invoice_paid: {
    title: "Payment Received!",
    icon: DollarSign,
    color: "text-emerald-400",
    bgColor: "bg-emerald-500/20",
    message: (data) => `${data.clientName} paid ${formatCurrency(data.amount)}!`
  },
  revenue_milestone: {
    title: "Milestone Reached!",
    icon: TrendingUp,
    color: "text-indigo-400",
    bgColor: "bg-indigo-500/20",
    message: (data) => `You've earned ${formatCurrency(data.milestone)} this ${data.period || 'month'}!`
  }
};

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
}

export default function WinSummary({ type = 'deal_won', data = {}, onClose, onShare }) {
  const [visible, setVisible] = useState(false);
  
  const winConfig = winTypes[type] || winTypes.deal_won;
  const Icon = winConfig.icon;

  useEffect(() => {
    // Animate in
    setTimeout(() => setVisible(true), 50);
    
    // Trigger celebratory confetti
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

    function randomInRange(min, max) {
      return Math.random() * (max - min) + min;
    }

    const interval = setInterval(function() {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);
      
      // Confetti from left and right
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      });
    }, 250);

    return () => clearInterval(interval);
  }, []);

  function handleClose() {
    setVisible(false);
    setTimeout(() => onClose && onClose(), 300);
  }

  return (
    <div 
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
      data-testid="win-summary-modal"
    >
      <Card 
        className={`bg-slate-900 border-slate-700 max-w-md w-full mx-4 transform transition-all duration-500 ${
          visible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
        }`}
      >
        <CardContent className="p-8 text-center relative">
          {/* Close button */}
          <button 
            onClick={handleClose}
            className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors"
            data-testid="win-summary-close"
          >
            <X className="w-5 h-5" />
          </button>
          
          {/* Icon with glow effect */}
          <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full ${winConfig.bgColor} mb-6 animate-pulse`}>
            <Icon className={`w-10 h-10 ${winConfig.color}`} />
          </div>
          
          {/* Title */}
          <h2 className={`text-3xl font-bold ${winConfig.color} mb-3`}>
            {winConfig.title}
          </h2>
          
          {/* Message */}
          <p className="text-xl text-white mb-2">
            {winConfig.message(data)}
          </p>
          
          {/* Sub-message */}
          <p className="text-slate-400 mb-6">
            Keep up the great work!
          </p>
          
          {/* Stats row (if applicable) */}
          {data.stats && (
            <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-slate-800/50 rounded-lg">
              {data.stats.thisMonth !== undefined && (
                <div>
                  <p className="text-2xl font-bold text-white">{formatCurrency(data.stats.thisMonth)}</p>
                  <p className="text-xs text-slate-400">This Month</p>
                </div>
              )}
              {data.stats.dealsWon !== undefined && (
                <div>
                  <p className="text-2xl font-bold text-white">{data.stats.dealsWon}</p>
                  <p className="text-xs text-slate-400">Deals Won</p>
                </div>
              )}
              {data.stats.winRate !== undefined && (
                <div>
                  <p className="text-2xl font-bold text-white">{data.stats.winRate}%</p>
                  <p className="text-xs text-slate-400">Win Rate</p>
                </div>
              )}
            </div>
          )}
          
          {/* Action buttons */}
          <div className="flex gap-3 justify-center">
            {onShare && (
              <Button 
                variant="outline" 
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
                onClick={onShare}
                data-testid="win-summary-share"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share Win
              </Button>
            )}
            <Button 
              className="bg-indigo-500 hover:bg-indigo-600 text-white"
              onClick={handleClose}
              data-testid="win-summary-continue"
            >
              Continue
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
